import { useReducer, useCallback } from 'react';
import { v1 as uuid } from 'uuid';
import { getErrorMessage } from '@trukt/utils/getErrorMessage';
import { ProgressNotifierContext } from './ProgressNotifierContext';
import {
  ProgressNotifierContextProps,
  ProgressNotfierActionTypes,
  UpdateProgressNotifierItemData,
  ProgressNotifierContextData,
} from './ProgressNotifierContext.types';
import { ProgressNotifierReducer } from './ProgressNotifierProvider.reducer';
import { ProgressNotifierItemData, ProgressNotifier } from './components';

export const ProgressNotifierProvider = ({ children }: ProgressNotifierContextProps) => {
  const [state, dispatch] = useReducer(ProgressNotifierReducer, []);

  const dispatchNotification: ProgressNotifierContextData['dispatchNotification'] = ({
    id = uuid(),
    ...rest
  }) => {
    dispatch({
      type: ProgressNotfierActionTypes.DISPATCH_NOTIFCATION,
      // we are not putting id after rest because there are parts of the app that are dependent on sending in an
      // id to dispatch the notification
      payload: { id, ...rest },
    });

    return id;
  };

  const dispatchNotifications = useCallback((items: ProgressNotifierItemData[]) => {
    dispatch({
      type: ProgressNotfierActionTypes.DISPATCH_NOTIFCATIONS,
      payload: items,
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    dispatch({
      type: ProgressNotfierActionTypes.REMOVE_NOTIFICATION,
      payload: { id },
    });
  }, []);

  const removeNotifications = useCallback((ids: string[]) => {
    dispatch({
      type: ProgressNotfierActionTypes.REMOVE_NOTIFICATIONS,
      payload: { ids },
    });
  }, []);

  const updateNotification = useCallback((id: string, item: UpdateProgressNotifierItemData) => {
    dispatch({
      type: ProgressNotfierActionTypes.UPDATE_NOTIFICATION,
      payload: { id, item },
    });
  }, []);

  const handleError: ProgressNotifierContextData['handleError'] = (label, error, id) => {
    if (id) {
      updateNotification(id, {
        isError: true,
        errorMessage: getErrorMessage(error),
      });
      return id;
    }
    return dispatchNotification({
      label,
      isError: true,
      errorMessage: getErrorMessage(error),
    });
  };

  return (
    <ProgressNotifierContext.Provider
      value={{
        state,
        dispatchNotification,
        dispatchNotifications,
        removeNotification,
        removeNotifications,
        updateNotification,
        handleError,
      }}
    >
      <>
        <ProgressNotifier items={state} onRemove={removeNotification} />
        {children}
      </>
    </ProgressNotifierContext.Provider>
  );
};
